import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { useFormField } from "../../lib/form-field-context.js";

/* ---------------- Switch ---------------- */

export interface SwitchProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "checked" | "defaultChecked" | "type" | "role"
> {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: React.ReactNode;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  {
    checked,
    defaultChecked,
    onChange,
    label,
    disabled,
    id,
    className,
    "aria-describedby": ariaDescribedby,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    ...rest
  },
  ref,
) {
  const autoId = React.useId();
  const sid = id ?? autoId;
  const isControlled = checked !== undefined;
  const [internal, setInternal] = React.useState(!!defaultChecked);
  const value = isControlled ? !!checked : internal;
  const field = useFormField();
  return (
    <label
      htmlFor={sid}
      className={cn(
        "inline-flex items-center gap-2 cursor-pointer select-none",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      <span className="relative inline-flex">
        <input
          {...rest}
          ref={ref}
          id={sid}
          type="checkbox"
          role="switch"
          checked={value}
          disabled={disabled}
          aria-describedby={field?.describedBy ?? ariaDescribedby}
          aria-invalid={field?.invalid ?? ariaInvalid}
          aria-required={field?.required ?? ariaRequired}
          onChange={(e) => {
            if (!isControlled) setInternal(e.target.checked);
            onChange?.(e.target.checked);
          }}
          className="peer sr-only"
        />
        {/* The thumb slides on `translate` (ADR-0092) — flipping a flex
            justification cannot animate, so it used to teleport. Travel:
            the w-9 track is 36px border-box, minus 1px border each side,
            minus the 2px inset each end, minus the 14px thumb = 16px. */}
        <span
          className={cn(
            "relative w-9 h-5 rounded-full border transition-colors",
            value
              ? "bg-pb-teal border-pb-teal"
              : field?.invalid
                ? "bg-pb-card-elevated border-pb-red"
                : "bg-pb-card-elevated border-pb-control-border",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-pb-teal/40",
          )}
        >
          <span
            className={cn(
              "absolute top-1/2 start-0.5 -translate-y-1/2 w-3.5 h-3.5 rounded-full",
              "transition-[translate,background-color] duration-motion-state ease-motion-standard",
              value
                ? "translate-x-4 rtl:-translate-x-4 bg-white"
                : "translate-x-0 bg-pb-text-muted",
            )}
          />
        </span>
      </span>
      {label && <span className="text-sm text-pb-text">{label}</span>}
    </label>
  );
});
